import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

interface TimeLeft {
  hours: number;
  minutes: number;
  seconds: number;
}

interface Deal {
  id: string;
  name: string;
  category: string;
  original_price: number;
  deal_price: number;
  image_url: string;
  badge: string;
  badge_color: string;
  tag: string;
  link: string;
  is_active: boolean;
  sort_order: number;
}

function getEndOfDay(): Date {
  const now = new Date();
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);
  return end;
}

function calcTimeLeft(endTime: Date): TimeLeft {
  const diff = Math.max(0, endTime.getTime() - Date.now());
  return {
    hours: Math.floor(diff / (1000 * 60 * 60)),
    minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((diff % (1000 * 60)) / 1000),
  };
}

function pad(n: number) {
  return String(n).padStart(2, '0');
}

function FlipUnit({ value, label }: { value: number; label: string }) {
  const [prev, setPrev] = useState(value);
  const [flip, setFlip] = useState(false);

  useEffect(() => {
    if (value !== prev) {
      setFlip(true);
      const t = setTimeout(() => { setFlip(false); setPrev(value); }, 350);
      return () => clearTimeout(t);
    }
  }, [value, prev]);

  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative w-12 h-12 rounded-xl overflow-hidden" style={{ background: 'linear-gradient(145deg, #1a1a2e, #16213e)' }}>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-white font-black text-xl tabular-nums">{pad(prev)}</span>
        </div>
        {flip && (
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{
              animation: 'flipDown 0.35s ease forwards',
              background: 'linear-gradient(145deg, #0f0f1a, #0d1117)',
            }}
          >
            <span className="text-white font-black text-xl tabular-nums">{pad(value)}</span>
          </div>
        )}
        <div className="absolute left-0 right-0 top-1/2 h-px bg-black/30 pointer-events-none"></div>
      </div>
      <span className="text-[9px] font-black uppercase tracking-widest text-gray-400">{label}</span>
    </div>
  );
}

export default function TodaysDeals() {
  const endTimeRef = useRef<Date>(getEndOfDay());
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(calcTimeLeft(endTimeRef.current));
  const [urgency, setUrgency] = useState(false);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch active deals from Supabase
  useEffect(() => {
    const fetchDeals = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('today_deals')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      if (!error && data) setDeals(data as Deal[]);
      setLoading(false);
    };
    fetchDeals();

    // Realtime subscription — jab admin deal update kare, home page pe turant reflect ho
    const channel = supabase
      .channel('today_deals_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'today_deals' }, () => {
        fetchDeals();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  useEffect(() => {
    const tick = setInterval(() => {
      const tl = calcTimeLeft(endTimeRef.current);
      setTimeLeft(tl);
      setUrgency(tl.hours === 0 && tl.minutes < 30);
    }, 1000);
    return () => clearInterval(tick);
  }, []);

  // Don't render if no active deals
  if (!loading && deals.length === 0) return null;

  return (
    <section className="py-8 bg-white overflow-hidden">
      <div className="max-w-2xl mx-auto px-4">

        {/* ── Header ── */}
        <div className="relative rounded-2xl overflow-hidden mb-5 p-4" style={{ background: 'linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 50%, #16213e 100%)' }}>
          <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle, #fff 1.5px, transparent 1.5px)', backgroundSize: '20px 20px' }}></div>
          <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full opacity-20" style={{ background: 'radial-gradient(circle, #f97316, transparent)' }}></div>

          <div className="relative z-10 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black" style={{ background: 'rgba(253,224,71,0.95)', color: '#7c2d12' }}>
                  <i className="ri-fire-fill text-orange-600 text-[10px]"></i>
                  {urgency ? 'Ending Soon!' : "Today's Deals"}
                </span>
              </div>
              <h2 className="text-white font-black text-xl leading-tight">
                Flash <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-400">Sale</span>
              </h2>
              <p className="text-gray-400 text-[11px] mt-0.5">Deals reset at midnight — grab fast!</p>
            </div>

            <div className="shrink-0">
              <p className="text-gray-500 text-[9px] font-bold uppercase tracking-widest text-center mb-1.5">Ends in</p>
              <div className="flex items-end gap-1.5">
                <FlipUnit value={timeLeft.hours} label="HRS" />
                <span className="text-white font-black text-lg mb-3 leading-none">:</span>
                <FlipUnit value={timeLeft.minutes} label="MIN" />
                <span className={`font-black text-lg mb-3 leading-none ${urgency ? 'text-red-400' : 'text-white'}`}>:</span>
                <FlipUnit value={timeLeft.seconds} label="SEC" />
              </div>
            </div>
          </div>

          {urgency && (
            <div className="relative z-10 mt-3 flex items-center gap-2 px-3 py-2 rounded-xl" style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)' }}>
              <i className="ri-alarm-warning-fill text-red-400 text-sm animate-pulse"></i>
              <p className="text-red-300 text-xs font-bold">Less than 30 minutes left — hurry up!</p>
            </div>
          )}
        </div>

        {/* ── Loading skeleton ── */}
        {loading && (
          <div className="grid grid-cols-2 gap-3">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="rounded-2xl overflow-hidden border border-gray-100 animate-pulse">
                <div className="h-40 bg-gray-100"></div>
                <div className="p-3 space-y-2">
                  <div className="h-3 bg-gray-100 rounded-full w-2/3"></div>
                  <div className="h-4 bg-gray-100 rounded-full w-full"></div>
                  <div className="h-3 bg-gray-100 rounded-full w-1/2"></div>
                  <div className="h-8 bg-gray-100 rounded-xl"></div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Deal Cards ── */}
        {!loading && deals.length > 0 && (
          <div className="grid grid-cols-2 gap-3">
            {deals.map((deal, idx) => {
              const savePct = Math.round((1 - deal.deal_price / deal.original_price) * 100);
              const saveAmt = deal.original_price - deal.deal_price;
              return (
                <Link
                  key={deal.id}
                  to={deal.link}
                  className="group block rounded-2xl overflow-hidden cursor-pointer active:scale-95 transition-transform duration-150"
                  style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.07)', boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}
                >
                  {/* Image */}
                  <div className="relative overflow-hidden" style={{ height: '160px' }}>
                    <img
                      src={deal.image_url}
                      alt={deal.name}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>

                    <div className="absolute top-2 left-2">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-black text-white bg-gradient-to-r ${deal.badge_color}`}>
                        {deal.badge}
                      </span>
                    </div>

                    <div className="absolute top-2 right-2">
                      <span className="px-2 py-0.5 rounded-full text-[9px] font-black text-white" style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(8px)' }}>
                        {deal.tag}
                      </span>
                    </div>

                    <div className="absolute bottom-2 left-2">
                      <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black text-white" style={{ background: 'rgba(16,185,129,0.85)', backdropFilter: 'blur(6px)' }}>
                        <i className="ri-price-tag-3-fill text-[8px]"></i>
                        Save ₹{saveAmt}
                      </span>
                    </div>
                  </div>

                  {/* Info */}
                  <div className="p-2.5">
                    <p className="text-[9px] font-black uppercase tracking-widest text-gray-400 mb-0.5">{deal.category}</p>
                    <p className="text-gray-900 font-black text-xs leading-tight line-clamp-1 mb-1.5">{deal.name}</p>

                    <div className="flex items-center gap-1.5 mb-2">
                      <span className="text-gray-900 font-black text-base leading-none">₹{deal.deal_price}</span>
                      <span className="text-gray-300 line-through text-[11px]">₹{deal.original_price}</span>
                    </div>

                    <div className="mb-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[9px] text-gray-400 font-semibold">Selling fast</span>
                        <span className="text-[9px] font-black text-red-500">{savePct}% off</span>
                      </div>
                      <div className="w-full h-1.5 rounded-full bg-gray-100 overflow-hidden">
                        <div
                          className={`h-full rounded-full bg-gradient-to-r ${deal.badge_color}`}
                          style={{ width: `${Math.min(95, 40 + idx * 12)}%`, transition: 'width 1s ease' }}
                        ></div>
                      </div>
                    </div>

                    <div className="w-full py-2 rounded-xl text-[11px] font-black text-white text-center" style={{ background: 'linear-gradient(135deg, #1a1a2e, #16213e)' }}>
                      Grab Deal →
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {/* Bottom strip */}
        {!loading && deals.length > 0 && (
          <div className="mt-4 flex items-center justify-between px-4 py-3 rounded-2xl" style={{ background: 'linear-gradient(135deg, #fff7ed, #fff1f2)', border: '1px solid rgba(249,115,22,0.15)' }}>
            <div className="flex items-center gap-2">
              <i className="ri-time-line text-orange-500 text-base"></i>
              <p className="text-gray-700 text-xs font-bold">New deals drop every day at midnight</p>
            </div>
            <Link to="/category" className="text-orange-600 text-xs font-black whitespace-nowrap flex items-center gap-0.5 hover:text-orange-700 transition-colors">
              All Deals <i className="ri-arrow-right-s-line"></i>
            </Link>
          </div>
        )}

      </div>

      <style>{`
        @keyframes flipDown {
          0%   { transform: rotateX(0deg); opacity: 1; }
          50%  { transform: rotateX(-90deg); opacity: 0.3; }
          100% { transform: rotateX(0deg); opacity: 1; }
        }
      `}</style>
    </section>
  );
}
